import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Divider, Typography, Spin, Alert, Tag, Space } from 'antd';
import { getProductOrderById } from '@/services/cards';

const { Title, Text } = Typography;

interface PickupLocationSchema {
  id: string;
  type: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  country: string;
  postal_code: number;
  contact: { name: string; phone_number: string; email?: string };
  created: number;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  cardId: string | null;
  cardData?: any;
  batchId?: string;
  showCardNumber?: boolean; // Add showCardNumber prop
}

const BatchCardDetailsModal: React.FC<Props> = ({ 
  visible, 
  onClose, 
  cardId, 
  cardData, 
  batchId,
  showCardNumber = false // Default to false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [batchDetails, setBatchDetails] = useState<any>(null);

  useEffect(() => {
    if (!cardId || !visible || !batchId) return;

    const fetchBatchDetails = async () => {
      try {
        const response = await getProductOrderById(batchId, false, true);
        // Get the first product order since that's where our data is
        setBatchDetails(response.product_orders?.[0] || null);
      } catch (err: any) {
        console.error('Failed to fetch batch details:', err);
      }
    };

    if (cardData) {
      setData(cardData);
      setLoading(false);
      setError(null);
      // Fetch batch details to get delivery information
      fetchBatchDetails();
    } else {
      setData({});
      setLoading(false);
      setError(null);
    }
  }, [cardId, visible, cardData, batchId]);

  // Helper to show N/A for missing or placeholder card details
  const displayValue = (val: any, placeholderVals: any[] = []) => {
    if (!val || placeholderVals.includes(val)) return 'N/A';
    return val;
  };

  // Helper to format status and delivery type
  const formatDeliveryType = (type: string) => {
    if (!type) return <span>N/A</span>;
    if (type === 'pickup-location') return <Tag color='orange'>PICKUP</Tag>;
    if (type === 'delivery') return <Tag color='blue'>DELIVERY</Tag>;
    return <Tag>{type.toUpperCase()}</Tag>;
  };
  const formatStatus = (status: string) => {
    if (!status) return <span>N/A</span>;
    const colorMap: Record<string, string> = {
      'REQUESTED': 'blue',
      'PENDING-FUNDING': 'orange',
      'PENDING-CREATION': 'orange',
      'PENDING-COLLECTION': 'yellow',
      'FUNDED': 'green',
      'COMPLETED': 'green',
      'DECLINED': 'red',
    };
    const upper = status.toUpperCase();
    return <Tag color={colorMap[upper] || 'default'}>{upper}</Tag>;
  };

  const destAddress: PickupLocationSchema | undefined = batchDetails?.destination_address;

  return (
    <Modal
      title="Card Details"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : data ? (
        <>
          <Descriptions column={1} bordered size="middle">
            {showCardNumber && data.bin && data.last_four && (
              <Descriptions.Item label="Card Number">
                <Space>
                  <Tag>{data.bin}</Tag>
                  <span>••••</span>
                  <Text code>{data.last_four}</Text>
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Holder Name">{displayValue(data.holder_name)}</Descriptions.Item>
            <Descriptions.Item label="Card Brand">{displayValue(data.brand)}</Descriptions.Item>
            <Descriptions.Item label="Currency">{displayValue(data.currency)}</Descriptions.Item>
            <Descriptions.Item label="Delivery Date">{data.delivery_date ? new Date(data.delivery_date).toLocaleDateString() : '-'}</Descriptions.Item>
            <Descriptions.Item label="Delivery Type">{formatDeliveryType(data.delivery_type)}</Descriptions.Item>
            <Descriptions.Item label="Status">{formatStatus(data.status)}</Descriptions.Item>
            <Descriptions.Item label="Created">{data.created ? new Date(data.created).toLocaleString() : '-'}</Descriptions.Item>
          </Descriptions>

          {destAddress && (
            <>
              <Divider />
              <Title level={5}>Delivery Information</Title>
              <Descriptions column={1} bordered size="middle">
                <Descriptions.Item label="Address Line 1">{destAddress.address_line1}</Descriptions.Item>
                {destAddress.address_line2 && (
                  <Descriptions.Item label="Address Line 2">{destAddress.address_line2}</Descriptions.Item>
                )}
                <Descriptions.Item label="City">{destAddress.city}</Descriptions.Item>
                <Descriptions.Item label="State">{destAddress.state}</Descriptions.Item>
                <Descriptions.Item label="Country">{destAddress.country}</Descriptions.Item>
                <Descriptions.Item label="Postal Code">{destAddress.postal_code}</Descriptions.Item>
                <Descriptions.Item label="Contact Name">{destAddress.contact.name}</Descriptions.Item>
                <Descriptions.Item label="Contact Phone">{destAddress.contact.phone_number}</Descriptions.Item>
                {destAddress.contact.email && (
                  <Descriptions.Item label="Contact Email">{destAddress.contact.email}</Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}
        </>
      ) : (
        <Text type="secondary">No details found.</Text>
      )}
    </Modal>
  );
};

export default BatchCardDetailsModal; 